import { Stack } from '@ltvco/refresh-lib/theme';
import { useReportRedirect, getVehiclePermalink } from '@ltvco/refresh-lib/v2';
import { useCallback, useState } from 'react';

import { VehicleMatchmakerResultCard } from './VehicleMatchmakerResultCard';

import type { VehicleMatch } from '@ltvco/refresh-lib/api';

export interface IVehicleMatchmakerResultProps {
  matches?: VehicleMatch[];
  /** When provided, opens report in ChatAI panel */
  onDetailsClick?: (match: VehicleMatch) => Promise<void>;
  /** Key of the match whose report is currently open; that card's Details button is disabled */
  openReportMatchKey?: string | null;
}

/**
 * VehicleMatchmakerResult - Displays recommended vehicle matches
 * @component
 */
export const VehicleMatchmakerResult = ({
  matches,
  onDetailsClick,
  openReportMatchKey,
}: IVehicleMatchmakerResultProps) => {
  const { redirectToReportUrl } = useReportRedirect();
  const [loadingMatchKey, setLoadingMatchKey] = useState<string | null>(null);

  const handleDetailsClick = useCallback(
    async (match: VehicleMatch, index: number) => {
      setLoadingMatchKey(String(index));
      try {
        if (onDetailsClick) {
          await onDetailsClick(match);
        } else {
          const result = await getVehiclePermalink({
            year: String(match.year),
            make: match.make,
            model: match.model,
            trim: match.trim ?? 'Base',
          });
          if (result.permalink) {
            redirectToReportUrl('vehicle', result.permalink);
          }
        }
      } catch {
        setLoadingMatchKey(null);
      } finally {
        setLoadingMatchKey(null);
      }
    },
    [onDetailsClick, redirectToReportUrl],
  );

  if (!matches || matches.length === 0) {
    return null;
  }

  return (
    <Stack width="100%" spacing={3}>
      <Stack spacing={2}>
        {matches.map((match, index) => (
          <VehicleMatchmakerResultCard
            index={index}
            key={`${match.year}-${match.make}-${match.model}-${index}`}
            match={match}
            disableButton={loadingMatchKey === String(index)}
            showTopMatchTag={index === 0}
            onDetailsClick={handleDetailsClick}
            openReportMatchKey={openReportMatchKey}
          />
        ))}
      </Stack>
    </Stack>
  );
};
